import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import { Pda } from './components/Pda';
import { Header } from './components/Header';
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from './components/ResultTable';
import { ScanInput } from './components/ScanInput';
import { isTrackingNumber, isValidBarCode } from 'utils/BarCodeUtil';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { parseException } from 'utils/exceptionUtils';
import { AutoComplete } from './components/AutoComplete';
import { GET_WAREHOUSE_BY_DESTINATION, GetWarehouseByDestinationResponse, GetWarehouseByDestinationVariables } from '../../graphql/queries/warehouseQueries';
import { t } from '../../i18n';
import { formatLocalizedTime } from '../../utils/dateFormatUtils';
import { SortingScanOverlay, SortingScanResponse } from './components/SortingScanOverlay';
import { getClientReporter } from '../../utils/clientReport/clientReporterRegistry';
import { ClientReportOpType } from '../../utils/clientReport/clientReportTypes';
import { safeJsonStringify, truncateToLength } from '../../utils/clientReport/clientReportUtils';
import { ScanResultEnvelope, ScanResultPrompt, useScanResultPrompt, ClientError, buildClientError, getEnvelopeFailedReason } from './scanResult';
import { playScanFailedAoSound } from '../../utils/scanUtils';

import type { ScanPromptInfo } from './scanResult';

// 与后端 SortingScanResponse 对齐：mutation 直接返回 SortingScanResponse，无外壳包裹
interface SortingScanDataGql {
    success: boolean;
    carrier?: string | null;
    sortingCode?: string | null;
    trackingNumber?: string | null;
    dspName?: string | null;
    promptInfo?: ScanPromptInfo | null;
    message?: string | null;
}

interface HandleScanResult {
    result: SortingScanDataGql;
}

const HANDLE_WHS_SORTING_SCAN_EVENT = gql`
    mutation HandleWhsSortingScanEvent($destinations: [String!]!, $barCode: String!, $clientReportKey: String) {
        result: handleWhsSortingScanEvent(destinations: $destinations, barCode: $barCode, clientReportKey: $clientReportKey) {
            success
            message
            carrier
            sortingCode
            trackingNumber
            dspName
            promptInfo {
                messageKey
                popupType
                title
                content
            }
        }
    }
`;

interface PageBodyProps {
    scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const PageBody = ({ scanPrompt }: PageBodyProps) => {
    const sortingScanClientReporter = getClientReporter(ClientReportOpType.SORTING_SCAN);

    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [destinations, setDestinations] = useState<string[]>([]);
    const [destinationInput, setDestinationInput] = useState('');
    const [destinationOptions, setDestinationOptions] = useState<Array<{ label: string, value: string }>>([]);
    const [scanValue, setScanValue] = useState('');
    const value2Ref = useRef<HTMLInputElement>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayData, setOverlayData] = useState<SortingScanResponse>();

    const [handleWhsSortingScanEvent] = useMutation<HandleScanResult>(HANDLE_WHS_SORTING_SCAN_EVENT);

    const scanPromptRef = useRef(scanPrompt);
    scanPromptRef.current = scanPrompt;
    const destinationsRef = useRef<string[]>(destinations);
    destinationsRef.current = destinations;
    /** 飞行中的 barCode 集合；同 barCode 在飞行期间再扫触发 NOTICE，不入队、不调后端、不记录 */
    const inFlightCodesRef = useRef<Set<string>>(new Set());
    /** 目的地查询是否因网络失败过；有网时 onCompleted 重置为 false */
    const destQueryNetworkErrorRef = useRef(false);

    const [getWarehouseByDestination] = useLazyQuery<
        GetWarehouseByDestinationResponse,
        GetWarehouseByDestinationVariables
    >(GET_WAREHOUSE_BY_DESTINATION, {
        onCompleted: (data) => {
            destQueryNetworkErrorRef.current = false;
            const warehouses = data.getWarehouseByDestination || [];
            if (warehouses.length === 0) {
                // 目的地查询无结果：纯前端预校验
                scanPromptRef.current.showResult(
                    buildClientError(ClientError.SORTING_DESTINATION_NOT_FOUND)
                );
                setDestinationOptions([]);
                return;
            }
            setDestinationOptions(warehouses.map(item => ({ label: item, value: item })));
        },
        onError: () => {
            playScanFailedAoSound();
            destQueryNetworkErrorRef.current = true;
            setDestinationOptions([]);
            scanPromptRef.current.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.SORTING_SCAN_TITLE));
        }
    });

    const onProcess = useCallback(async (event: { id: string; seq: number; code: string; ts: number }) => {
        const barCode = event.code;
        const clientReportKey = sortingScanClientReporter.createReportKey(barCode);
        void sortingScanClientReporter.onScanTriggered(clientReportKey, barCode);

        const finish = () => {
            inFlightCodesRef.current.delete(barCode);
        };

        // 预校验 1：运单号格式
        if (!isTrackingNumber(barCode)) {
            playScanFailedAoSound();
            const env = buildClientError(ClientError.INVALID_TRACKING_NUMBER);
            scanPromptRef.current.showResult(env, barCode);
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode, failedReason: getEnvelopeFailedReason(env), time: formatLocalizedTime(), seq: event.seq
            })));
            void sortingScanClientReporter.onIntercepted(clientReportKey, `invalid_tracking_number:${barCode}`);
            finish();
            return;
        }

        value2Ref.current?.blur();
        setScanValue(barCode);

        // 预校验 2：必须先选目的地；若目的地为空且之前因网络失败，优先报网络错误
        const currentDestinations = destinationsRef.current;
        if (currentDestinations.length === 0) {
            playScanFailedAoSound();
            const env = destQueryNetworkErrorRef.current
                ? buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.SORTING_SCAN_TITLE)
                : buildClientError(ClientError.SORTING_PLEASE_SELECT_DESTINATION);
            scanPromptRef.current.showResult(env, barCode);
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode, failedReason: getEnvelopeFailedReason(env), time: formatLocalizedTime(), seq: event.seq
            })));
            void sortingScanClientReporter.onIntercepted(clientReportKey, getEnvelopeFailedReason(env));
            finish();
            return;
        }

        // 调后端
        void sortingScanClientReporter.onRequest(clientReportKey, { destinations: currentDestinations, barCode });
        let responseRecorded = false;
        let responseData: any = null;
        try {
            const { data } = await handleWhsSortingScanEvent({
                variables: { destinations: currentDestinations, barCode, clientReportKey }
            });
            const raw = data?.result;
            // mutation 直接返回 SortingScanResponse，raw 本身即为 data 载体（含 promptInfo）
            const env: ScanResultEnvelope<SortingScanDataGql> = {
                success: raw?.success ?? false,
                message: raw?.message ?? null,
                data: raw ?? null,
            };
            scanPromptRef.current.showResult(env, barCode);

            responseData = raw;
            sortingScanClientReporter.onResponse(clientReportKey, responseData)
                .then(() => { responseRecorded = true; })
                .catch(() => {
                    void sortingScanClientReporter.reportDirectly(clientReportKey, {
                        responseAtMs: Date.now(),
                        responseResult: truncateToLength(safeJsonStringify(responseData), 300),
                    });
                    responseRecorded = true;
                });

            if (env.success) {
                // 成功：写成功列；有 carrier（真实业务扫描成功）才显示 overlay，
                // 无 carrier（如 ALREADY_SCANNED 幂等成功）只弹绿色 promptInfo 不显示 overlay
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode, time: formatLocalizedTime(), seq: event.seq
                })));
                if (env.data?.carrier) {
                    setOverlayData({
                        success: true,
                        carrier: env.data.carrier ?? '',
                        sortingCode: env.data.sortingCode ?? '',
                        trackingNumber: env.data.trackingNumber ?? '',
                        dspName: env.data.dspName ?? '',
                    } as SortingScanResponse);
                    setOverlayVisible(true);
                }
            } else {
                // 失败：写失败列；若后端通过 ScanFailureException 透传了 carrier 等业务上下文，
                // 也展示 SortingScanOverlay，错级弹窗（z-[60]）叠在 overlay（z-50）之上
                playScanFailedAoSound();
                const promptInfo = (env.data as any)?.promptInfo;
                const reason = [promptInfo?.title, promptInfo?.content].filter(Boolean).join('，') || env.message || t('pda.common.scanFailed');
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode, failedReason: reason, time: formatLocalizedTime(), seq: event.seq
                })));
                if (env.data?.carrier) {
                    setOverlayData({
                        success: false,
                        carrier: env.data.carrier ?? '',
                        sortingCode: env.data.sortingCode ?? '',
                        trackingNumber: env.data.trackingNumber ?? '',
                        dspName: env.data.dspName ?? '',
                    } as SortingScanResponse);
                    setOverlayVisible(true);
                }
            }
        } catch (error) {
            // Apollo/网络错误兜底
            playScanFailedAoSound();
            const { message: errorMessage } = parseException(error);
            scanPromptRef.current.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.SORTING_SCAN_TITLE), barCode);
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode, failedReason: errorMessage, time: formatLocalizedTime(), seq: event.seq
            })));
            responseData = { error: String(error), message: errorMessage };
            sortingScanClientReporter.onResponse(clientReportKey, responseData)
                .then(() => { responseRecorded = true; })
                .catch(() => {
                    void sortingScanClientReporter.reportDirectly(clientReportKey, {
                        responseAtMs: Date.now(),
                        responseResult: truncateToLength(safeJsonStringify(responseData), 300),
                    });
                });
        } finally {
            finish();
            if (!responseRecorded) {
                sortingScanClientReporter.onResponse(clientReportKey, responseData || {})
                    .catch(() => {
                        void sortingScanClientReporter.reportDirectly(clientReportKey, {
                            responseAtMs: Date.now(),
                            responseResult: responseData ? truncateToLength(safeJsonStringify(responseData), 300) : '',
                        });
                    });
            }
        }
    }, [handleWhsSortingScanEvent, sortingScanClientReporter]);

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    const handleScan = (code: string) => {
        if (!isValidBarCode(code)) {
            return;
        }
        if (inFlightCodesRef.current.has(code)) {
            // 飞行队列重复扫描：黑底 NOTICE 1.5s，不入队
            scanPromptRef.current.showNotice(t('pda.scan.duplicateScan'), code);
            return;
        }
        scanPromptRef.current.clearPrompt();
        inFlightCodesRef.current.add(code);
        enqueueScan(code);
    };

    const successColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
    ];
    const failedColumns: Column[] = [
        ...successColumns,
        { title: t('pda.common.failedReason'), key: 'failedReason' },
    ];

    const handleDestinationInputChange = (value: string) => {
        setDestinationInput(value);
        if (value.trim()) {
            getWarehouseByDestination({ variables: { destination: value.trim() } });
        } else {
            setDestinationOptions([]);
        }
    };

    const handleDestinationSelect = (value: string) => {
        const trimmed = value.trim();
        if (trimmed && !destinations.includes(trimmed)) {
            setDestinations(prev => [...prev, trimmed]);
            setDestinationInput('');
            setDestinationOptions([]);
        }
    };

    const removeDestination = (index: number) => {
        setDestinations(prev => {
            const next = prev.filter((_, i) => i !== index);
            // 目的地全部清空时重置网络错误标记，避免再次扫描时误报 NETWORK_ERROR
            if (next.length === 0) destQueryNetworkErrorRef.current = false;
            return next;
        });
    };

    const handleOverlayClose = () => {
        setOverlayVisible(false);
        setOverlayData(undefined);
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="mb-2">
                <AutoComplete
                    className="w-full p-2 border rounded"
                    placeholder={t('pda.pages.sorting.destination')}
                    value={destinationInput}
                    onChange={handleDestinationInputChange}
                    onSelect={handleDestinationSelect}
                    options={destinationOptions}
                />
                {destinations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {destinations.map((dest, index) => (
                            <div key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                <span>{dest}</span>
                                <button
                                    onClick={() => removeDestination(index)}
                                    className="text-blue-600 hover:text-blue-800 font-bold ml-1"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <ScanInput
                ref={value2Ref}
                placeholder={t('pda.pages.sorting.trackingNO')}
                value={scanValue}
                onScan={handleScan}
            />
            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
            {overlayData && (
                <SortingScanOverlay
                    visible={overlayVisible}
                    data={overlayData}
                    onClose={handleOverlayClose}
                />
            )}
        </div>
    );
}

export default function SortingScanPage() {
    const scanPrompt = useScanResultPrompt();
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2 relative">
            {/* 错级反馈层（红/橙/黑底 NOTICE）；z-index 高于 SortingScanOverlay，叠在它之上 */}
            <ScanResultPrompt prompt={scanPrompt.prompt} barCode={scanPrompt.barCode} onClose={scanPrompt.clearPrompt} />
            <Header title={t('pda.pages.sorting.sortingScan')} />
            <Pda />
            <PageBody scanPrompt={scanPrompt} />
        </div>
    );
}